import React, { useState, useCallback } from 'react';
import { Dropdown, DropdownMenu, UncontrolledDropdown, DropdownItem, DropdownToggle, ListGroup, ListGroupItem, Navbar, ListGroupItemHeading, Col, Button, Row, Container, Media, Card, CardBody, CardDeck, CardGroup, FormGroup, ButtonGroup, InputGroup, Input } from 'reactstrap';
import { fetchParticipants, deleteParticipant, updateParticipant } from 'api/actions/agenda';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { uploadFileV2 } from 'api/actions/file';
import { toast } from 'components/Shared/Toast';
import AgendaModal from 'components/Modals/AgendaModal';
import { useTranslation } from 'react-i18next'
import { fetcher } from 'api/client';
import Dropzone from 'react-dropzone';
import clsx from 'clsx';
import useDebounce from 'hooks/useDebounce';
import { initialLetters } from 'utils';
import { Ellipsis } from 'components/Shared/Ellipsis';
import { MainHeader } from 'components/Shared/MainHeader';

function AgendaMenu({ handleDelete, participantId, handleEdit }) {
    const { t } = useTranslation();
    const doHandleEdit = () => {
        handleEdit(participantId)
    }
    const doHandleDelete = () => {
        handleDelete(participantId)
    }

    return (
        <UncontrolledDropdown direction="right">
            <DropdownToggle tag="span">
                <Ellipsis />
            </DropdownToggle>

            <DropdownMenu >
                <DropdownItem onClick={doHandleEdit}>
                    <i className="fas fa-edit" />{' '}{t('agenda.edit')}
                </DropdownItem>

                <DropdownItem className="text-danger" onClick={doHandleDelete}>
                    <i className="fas fa-trash" />{' '}{t('agenda.delete')}
                </DropdownItem>
            </DropdownMenu>
        </UncontrolledDropdown>
    )
}

const photoStyle = {
    border: '4px solid #FFF',
    borderRadius: '50% !important',
    boxShadow: '0 0 0 2px #f47b33',
    height: '70px',
    width: '70px',
};

function ParticipantItem({ participant, handleEdit, handleDelete, onDrop, openPopup }) {

    const { t } = useTranslation();

    const handleOpenPopup = () => {
        openPopup(participant._id)
    }

    return (
        <Col md="12" lg="4" className="my-2">
            <Card className="text-center rounded-lg card-shadow" >
                <CardBody>
                    <Media>
                        <Media className="align-self-center">
                            <Dropzone accept='image/*' onDrop={(files) => onDrop(files, participant._id)} multiple={false}>
                                {({ getRootProps, getInputProps }) => (
                                    <div {...getRootProps()}>
                                        <input {...getInputProps()} />
                                        {participant.photo ?
                                            <img
                                                src={participant.photo}
                                                style={photoStyle}
                                                className="pointer rounded-pill"
                                                width="79"
                                                alt="profile-img"
                                            />
                                            :
                                            <div
                                                style={{ ...photoStyle, display: 'table-cell', verticalAlign: 'middle' }}
                                                className="rounded-pill pointer"
                                            >
                                                <span className="font-weight-bold">
                                                    {initialLetters(participant?.name || participant.email, participant?.last_name)}
                                                </span>
                                            </div>
                                        }
                                    </div>
                                )}
                            </Dropzone>
                        </Media>
                        <Media body onClick={handleOpenPopup} className="pointer" style={{ minHeight: '110px', maxHeight: '110px' }}>
                            <div style={{height:"15px"}} className="pb-4">
                                <span className="d-inline-block text-truncate" style={{ maxWidth: '160px' }}>{participant.name || t('agenda.no_name')}</span>{' '}
                                <span className="d-inline-block text-truncate" style={{ maxWidth: '120px' }}>{participant.last_name}</span>
                            </div>
                            {
                                participant.email &&
                                <div className="mb-1">
                                    <span className="fas fa-envelope fa-fw text-muted"></span>{' '}
                                    <span className="text-muted text-truncate">{participant.email}</span>
                                </div>
                            }
                            {
                                participant.phone &&
                                <div className="mb-1">
                                    <span className="fas fa-phone fa-fw text-muted"></span>{' '}
                                    <span className="text-muted text-truncate">{participant.phone}</span>
                                </div>
                            }
                            {
                                participant.zipcode &&
                                <div className="mb-1">
                                    <span className="fas fa-map-marker fa-fw text-muted"></span>{' '}
                                    <span className="text-muted text-truncate">{participant.zipcode}</span>
                                </div>
                            }
                            {participant.documents?.length ?
                                <div className="mb-1">
                                    <span className="fas fas fa-file fa-fw text-muted"></span>{' '}
                                    <span className="text-muted text-truncate">{participant.documents?.length}</span>
                                </div>
                                :
                                null
                            }
                        </Media>
                        <div className="text-right" style={{ position: 'absolute', top: 0, right: 3 }}>
                            <AgendaMenu
                                handleDelete={handleDelete}
                                handleEdit={handleEdit}
                                participantId={participant._id}
                            />
                        </div>
                    </Media>
                </CardBody>
            </Card >
        </Col>
    )
}

const hasMore = (data = {}) => Number(data.page) < Number(data.pages);

function Agenda() {

    const { t } = useTranslation();

    const queryClient = useQueryClient();

    const [modalInfo, setmodalInfo] = useState({ isOpen: false });

    const [state, setState] = useState({ page: 1, search: '' })

    const debouncedSearch = useDebounce(state.search, 500);

    const { mutate: deleteParticipantMutation } = useMutation((payload) => fetcher(deleteParticipant(payload)), {
        onSuccess: () => {
            queryClient.invalidateQueries(['participants']);
            toast.success(t('agenda.success_delete'));
        }
    })

    const openPopup = (participantId) => {
        setmodalInfo({ isOpen: true, participantId, editMode: !participantId })
    }

    const toggle = () => {
        setmodalInfo({ isOpen: false })
    }

    const handleDelete = (participantId) => {
        deleteParticipantMutation(participantId);
    }

    const handleEdit = (participantId) => {
        setmodalInfo({ isOpen: true, participantId, editMode: true })
    }

    const handleSearch = (e) => {
        setState({ page: 1, search: e.target.value })
    }

    const doFetchParticipants = useCallback(({ queryKey }) => {
        const [, { page = '', search = '' }] = queryKey;
        return fetcher(fetchParticipants({ page, search, limit: 15 }))
    }, [])

    const {
        data: latestData,
        isFetching,
    } = useQuery(['participants', { page: state.page, search: debouncedSearch }], doFetchParticipants, { keepPreviousData: true })

    // useEffect(() => {
    //     if (hasMore(latestData)) {
    //         useQuery.prefetchQuery(['participants', state.page + 1], doFetchParticipants)
    //     }
    // }, [state, doFetchParticipants, latestData])

    const { mutate: updateParticipantMutation } = useMutation((payload) => fetcher(updateParticipant(payload.participantId, { photo: payload.url })), {
        onSuccess: async (data) => {
            queryClient.invalidateQueries(['participants']);
        }
    })

    const onDrop = async (files, participantId) => {
        const result = await fetcher(uploadFileV2(files[0]))
        updateParticipantMutation({ participantId, url: result.url })
    }

    return (
        <>
            <AgendaModal modalInfo={modalInfo} toggle={toggle} setmodalInfo={setmodalInfo} />

            <MainHeader title='agenda.title'>
                <span className={clsx('icon-button create-participant pointer')} title={t('agenda.create')} onClick={() => openPopup()}>
                    <i className="fas fa-plus fa-2x"></i>
                </span>
            </MainHeader>
            <Row>
                <Col md="12" className="mb-2">
                    <Card className="rounded-lg ">
                        <CardBody>
                            <InputGroup>
                                <Input onChange={handleSearch} placeholder={t('agenda.search')} value={state.search} />
                                <ButtonGroup>
                                    <Button
                                        color="light"
                                        onClick={() => setState({ ...state, page: Math.max(state.page - 1, 0) })}
                                        disabled={state.page === 1}>
                                        {isFetching ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-chevron-left" />}
                                    </Button>
                                    <Button color="light">{state.page}/{latestData?.pages}</Button>
                                    <Button
                                        color="light"
                                        onClick={() =>
                                            setState({ ...state, page: (!latestData || !hasMore(latestData) ? state.page : state.page + 1) })
                                        }
                                        disabled={!latestData || !hasMore(latestData)}>
                                        {isFetching ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-chevron-right" />}
                                    </Button>
                                </ButtonGroup>
                            </InputGroup>
                        </CardBody>
                    </Card>
                </Col>
                {latestData?.docs?.length ? latestData.docs.map((participant) => (
                    <ParticipantItem
                        key={participant._id}
                        participant={participant}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        onDrop={onDrop}
                        openPopup={openPopup}
                    />
                )) :
                    <Col md="12" className="mb-2">
                        <Card className="rounded-lg">
                            <CardBody className="text-center">
                                {t('agenda.empty')}
                            </CardBody>
                        </Card>
                    </Col>
                }
            </Row>
        </>
    )
}

export default Agenda;
